import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSetAllToolActionsEnabled } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { useToast } from 'hooks/use-toast';

/**
 * What the bulk button offers: turn on the actions that are off while any action is
 * off, otherwise turn every action off. ``null`` for a tool without actions.
 */
export const bulkEnableTarget = <T,>(
  actions: T[],
  isEnabled: (action: T) => boolean
): { enabled: boolean; count: number } | null => {
  if (actions.length === 0) return null;
  const off = actions.filter((action) => !isEnabled(action)).length;
  return off > 0 ? { enabled: true, count: off } : { enabled: false, count: actions.length };
};

/**
 * Turns every action of a tool on or off in one request. Imports enable reads only,
 * so a large spec would otherwise need one toggle per write action.
 */
export function ToolApiSetAllActionsEnabledModal({
  toolId,
  enabled,
  count,
  open,
  onOpenChange,
  projectKey,
  isBlocksTool,
}: {
  toolId: string;
  /** true: turn every action on; false: turn every action off. */
  enabled: boolean;
  /** How many actions the change affects, shown in the confirmation. */
  count: number;
  open: boolean;
  onOpenChange: (value: boolean) => void;
  projectKey: string;
  isBlocksTool?: boolean;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useSetAllToolActionsEnabled();

  const failed = t('FAILED_TO_UPDATE_ALL_ACTIONS') || 'Failed to update the actions';

  const handleConfirm = async () => {
    try {
      const response = await mutateAsync({ toolId, enabled, projectKey, isBlocksTool });
      if (!response.is_success) {
        throw new Error(response.message || response.detail || failed);
      }
      toast({
        variant: 'success',
        title: t('ALL_ACTIONS_UPDATED') || 'Actions updated',
        description: response.detail,
      });
      queryClient.invalidateQueries({ queryKey: ['tools', 'detail', toolId] });
      onOpenChange(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('UPDATE_FAILED') || 'Update failed',
        description: error instanceof Error ? error.message : failed,
      });
    }
  };

  const title = enabled
    ? t('ENABLE_ALL_ACTIONS') || 'Enable all actions'
    : t('DISABLE_ALL_ACTIONS') || 'Disable all actions';
  const description = enabled
    ? t('ENABLE_ALL_ACTIONS_CONFIRMATION', {
        count,
        defaultValue:
          'Turn on {{count}} action(s)? This includes actions that create, change or delete data. Actions set to require approval still ask before they run.',
      })
    : t('DISABLE_ALL_ACTIONS_CONFIRMATION', {
        count,
        defaultValue: 'Turn off {{count}} action(s)? Agents cannot use them until you turn them on again.',
      });
  const confirm = enabled ? t('ENABLE_ALL') || 'Enable all' : t('DISABLE_ALL') || 'Disable all';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>{t('CANCEL') || 'Cancel'}</AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              // Keep the dialog open until the request settles; it closes on success.
              event.preventDefault();
              void handleConfirm();
            }}
            disabled={isPending}
          >
            {isPending ? t('SAVING') || 'Saving...' : confirm}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
